// TestApp.jsx
import { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Stack,
  Modal,
  useMediaQuery,
  Drawer,
  List,
  Divider,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  TextField,
} from "@mui/material";
import AssistantPhotoIcon from "@mui/icons-material/AssistantPhoto";
import CloseIcon from "@mui/icons-material/Close";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";

import {
  useGetQuestionQuery,
  useGetAnswerChoiceQuery,
  useGetUserAnswerQuery,
  usePostUserAnswerMutation,
} from "../api";

import { useSystemMessage } from "../providers/SystemMessageContext";

/**
 * DrawerList - simple list of question titles with flagged indicator
 */
const DrawerList = ({ questionList = [], onNavigate, flaggedQuestions = {}, onClose }) => {
  return (
    <Box sx={{ width: 280 }} role="presentation" onClick={() => onClose(false)}>
      <List>
        {questionList.map((title, idx) => {
          const qnum = idx + 1;
          const flagged = Boolean(flaggedQuestions[String(qnum)]);
          return (
            <ListItem key={qnum} disablePadding>
              <ListItemButton
                onClick={() => {
                  onNavigate(qnum);
                }}
              >
                <ListItemText primary={title} />
                {flagged && <AssistantPhotoIcon sx={{ color: "#c44601", ml: 1 }} />}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Divider />
    </Box>
  );
};

/**
 * QuestionView - renders a single question, its choices, image, and navigation controls.
 *
 * Props:
 * - examComponentId: id of the exam component (passed as exam_component_id in your hooks)
 * - questionNumber: number (1..N)
 * - questionCount: total questions
 * - onNavigate(directionOrTarget): callback to change current question
 * - submitUserAnswer: mutation function returned from usePostUserAnswerMutation
 * - onComplete: callback when "Complete Exam" is clicked and finished
 */
const QuestionView = ({
  examComponentId,
  questionNumber,
  questionCount,
  onNavigate,
  submitUserAnswer,
  onComplete,
}) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const { showMessage } = useSystemMessage();

  const {
    data: answerChoiceData,
    loading: answerChoiceLoading,
    error: answerChoiceError,
  } = useGetAnswerChoiceQuery(null, examComponentId, questionNumber);

  const {
    data: questionData,
    loading: questionLoading,
    error: questionError,
  } = useGetQuestionQuery(null, examComponentId, questionNumber);

  const {
    data: userAnswerData,
    loading: userAnswerLoading,
    error: userAnswerError,
  } = useGetUserAnswerQuery(null, examComponentId, questionNumber);
  // useEffect(() => {
  //     if (userAnswerData) {
  //         showMessage(userAnswerData.message, userAnswerData.success ? "success" : "error");
  //     }
  // }, [userAnswerData]);

  // local state
  // selectedChoice is numeric option (1,2,3,4) or '' when none
  const [selectedChoice, setSelectedChoice] = useState("");
  const [writtenAnswer, setWrittenAnswer] = useState("");
  const writtenRef = useRef(writtenAnswer);
  const [isFlagged, setIsFlagged] = useState(false);
  const [imageOpen, setImageOpen] = useState(false);
  const [isEssayQuestion, setIsEssayQuestion] = useState(false);

  useEffect(() => {
    writtenRef.current = writtenAnswer;
  }, [writtenAnswer]);

  // Derived question object (your questionData contains .data array)
  const question = useMemo(() => {
    return questionData?.data?.[0] ?? null;
  }, [questionData]);

  const choices = useMemo(() => {
    // answerChoiceData.data is an array of { option: 1..4, text }
    if (!answerChoiceData?.data) return [];
    if (answerChoiceData.data.length === 0) {
      setIsEssayQuestion(true);
      return [];
    }
    // sort by option to keep order stable
    return [...answerChoiceData.data]
      .filter((c) => c.text != 'blank')
      .sort((a, b) => Number(a.option) - Number(b.option))
      .map((c) => ({ option: Number(c.option), text: c.text }));
  }, [answerChoiceData]);

  // Initialize selection & flag from userAnswerData when loaded
  useEffect(() => {
    if (!userAnswerData?.data?.length) {
      setIsFlagged(false);
      return;
    }
    const ua = userAnswerData.data[0];
    // user API returns selected_choice numeric or null
    setSelectedChoice(ua.selected_option ?? "");
    setWrittenAnswer(ua.written_answer ?? "");
    setIsFlagged(Boolean(ua.is_flagged));
  }, [userAnswerData]);

  // Handler for radio change (value will be string from MUI)
  const handleOptionChange = (ev) => {
    // keep numeric as number for submission later
    const val = ev.target.value;
    setSelectedChoice(val === "" ? "" : Number(val));
    handleSave(null, val);
  };

  const handleWritingChange = (e) => {
    const value = e.target.value;
    setWrittenAnswer(value ?? "");
  };

  const handleSaveWritten = async () => {
    try {
      // If nothing selected, we still call submit with selected_choice: null to indicate clearing
      const payload = {
        update: {
          exam_component_id: examComponentId,
          question_number: questionNumber,
          selected_choice: selectedChoice ?? null,
          written_answer: writtenAnswer ?? "",
          is_flagged: Boolean(isFlagged),
        }

      };
      await submitUserAnswer(payload);

    } catch (err) {
      console.error("Failed to save answer", err);
    }
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      submitUserAnswer({
        update: {
          exam_component_id: examComponentId,
          question_number: questionNumber,
          selected_choice: null,
          written_answer: writtenRef.current ?? "",
          is_flagged: Boolean(isFlagged),
        },
      });
    }, 10000);

    return () => clearInterval(intervalId);
  }, [examComponentId, questionNumber, isFlagged]);
  
  // Save (submit) answer and optionally navigate
  const handleSave = async (directionOrTarget = null, choiceOverride = null) => {
    // directionOrTarget: number delta (1,-1) or absolute question number
    if (choiceOverride) {
      try {
        const payload = {
          update: {
            exam_component_id: examComponentId,
            question_number: questionNumber,
            selected_choice: choiceOverride === "" ? null : Number(choiceOverride),
            writtenAnswer: writtenAnswer ?? null,
            is_flagged: Boolean(isFlagged),
          }

        };
        await submitUserAnswer(payload);
        return;

      } catch (err) {
        console.error("Failed to save answer", err);
        return;
      }
    };

    try {
      // If nothing selected, we still call submit with selected_choice: null to indicate clearing
      const payload = {
        update: {
          exam_component_id: examComponentId,
          question_number: questionNumber,
          selected_choice: selectedChoice === "" ? null : Number(selectedChoice),
          writtenAnswer: writtenAnswer ?? null,
          is_flagged: Boolean(isFlagged),
        }

      };

      // Use the mutation you already have; adjust shape if your API expects different key names
      await submitUserAnswer(payload);

      // After save, navigate if requested
      if (directionOrTarget == null) {
        // no navigation requested
      } else if (typeof directionOrTarget === "number" && Math.abs(directionOrTarget) === 1) {
        onNavigate(directionOrTarget);
      } else if (typeof directionOrTarget === "number") {
        // warp to absolute question number
        onNavigate(directionOrTarget);
      }
    } catch (err) {
      console.error("Failed to save answer", err);
    }
  };

  // Toggle flagged state and persist flag quickly (no navigation)
  const handleToggleFlag = async () => {
    const newFlag = !isFlagged;
    try {
      await submitUserAnswer({
        update: {
          exam_component_id: examComponentId,
          question_number: questionNumber,
          selected_choice: selectedChoice === "" ? null : Number(selectedChoice),
          is_flagged: newFlag,
        }
      });
      setIsFlagged(newFlag);
    } catch (err) {
      console.error("Failed to save flag state", err);
    }
  };


  // "Complete exam" behavior: save then call onComplete (which may navigate away)
  const handleComplete = async () => {
    await handleSave(); // save current answer
    await handleSaveWritten();
    if (typeof onComplete === "function") onComplete();
  };

  // Loading / error states
  if (questionLoading || answerChoiceLoading || userAnswerLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }
  if (questionError || answerChoiceError || userAnswerError) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Failed to load question. Try again later.</Typography>
      </Box>
    );
  }

  // rendering styles
  const bigQuestionStyle = { width: "50vw", margin: "0 auto", textAlign: "center" };
  const mobileQuestionStyle = { width: "94vw", margin: "0 auto", textAlign: "center" };

  return (
    <Box sx={!isMobile ? bigQuestionStyle : mobileQuestionStyle}>
      <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center" }}>
        <Typography variant="h5" sx={{ p: 1 }}>
          Question {question?.number ?? questionNumber}
        </Typography>

        <IconButton
          aria-label="flag-question"
          onClick={handleToggleFlag}
          sx={{
            color: isFlagged ? "#c44601" : "#595959",
            "&:hover": { color: isFlagged ? "#a03a00" : "#404040" },
            ml: 1,
          }}
        >
          <AssistantPhotoIcon />
        </IconButton>
      </Box>

      <Stack direction="row" spacing={2} justifyContent="center" sx={{ m: 2 }}>
        <Button variant="outlined" onClick={() => handleSave(-1)} disabled={questionNumber === 1}>
          Prev
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (questionNumber === questionCount) {
              handleComplete();
            } else {
              handleSave(1);
            }
          }}
        >
          {questionNumber === questionCount ? "Complete Exam" : "Next"}
        </Button>
      </Stack>

      <Typography variant="body1" sx={{ textAlign: "left" }}>
        <ReactMarkdown>{question?.above_text ?? ""}</ReactMarkdown>
      </Typography>

      {question?.image && (
        <>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              paddingTop: "56.25%",
              overflow: "hidden",
              cursor: "pointer",
              mt: 2,
            }}
            onClick={() => setImageOpen(true)}
          >
            <img
              src={`${process.env.REACT_APP_API_BASE_URL}${question.image}`}
              alt="question"
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "contain" }}
            />
          </Box>

          <Modal open={imageOpen} onClose={() => setImageOpen(false)}>
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                bgcolor: "rgba(0, 0, 0, 0.8)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              onClick={() => setImageOpen(false)}
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setImageOpen(false);
                }}
                sx={{ position: "absolute", top: 16, right: 16, color: "white" }}
              >
                <CloseIcon />
              </IconButton>

              <img
                src={`${process.env.REACT_APP_API_BASE_URL}${question.image}`}
                alt="question-large"
                style={{ maxWidth: "90vw", maxHeight: "100vh", objectFit: "contain" }}
              />
            </Box>
          </Modal>
        </>
      )}

      <Typography variant="body1" sx={{ textAlign: "left", mt: 4 }}>
        <ReactMarkdown>{question?.text ?? ""}</ReactMarkdown>
      </Typography>

      {(choices.length > 0) && (
        <Box sx={{ mt: 3, textAlign: "left" }}>
          <RadioGroup value={selectedChoice === "" ? "" : String(selectedChoice)} onChange={handleOptionChange}>
            {choices.map((c) => (
              <FormControlLabel key={c.option} value={String(c.option)} control={<Radio />} label={c.text} />
            ))}
          </RadioGroup>
        </Box>
      )}

      {isEssayQuestion && (
        <TextField  sx={{width:'100%'}}
          multiline
          rows={15}
          maxRows={15}
          value={writtenAnswer}
          onChange={handleWritingChange}
        />
      )}
    </Box>
  );
};

/**
 * TestApp - top-level wrapper that manages question number and drawer
 *
 * Props:
 * - componentName (string) -> component type in your backend
 * - questionCount (number)
 * - questionList (array of titles)
 * - examComponentId (number) optional - if not provided we read from localStorage 'exam_id'
 */
const TestApp = ({ questionCount = 1, questionList = [], examComponentId: propExamComponentId, examComponentBaseId = null }) => {
  const [questionNumber, setQuestionNumber] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [flagDict, setFlagDict] = useState({});
  const [examComponentId, setExamComponentId] = useState(propExamComponentId || null);
  const { data: userAnswerData, loading: userAnswerDataLoading, refresh: refreshUserAnswerData } = useGetUserAnswerQuery(null, examComponentId, null);
  const navigate = useNavigate();

  // mutation hook for submitting answers
  const { submit: submitUserAnswer } = usePostUserAnswerMutation();

  // read exam id from localStorage once (if not passed in)
  useEffect(() => {
    if (!propExamComponentId) {
      const stored = localStorage.getItem("exam_id");
      if (stored) setExamComponentId(Number(stored));
    }
  }, [propExamComponentId]);

  // toggle drawer and fetch flagged questions when opening
  const fetchFlagDict = () => {
    refreshUserAnswerData();
  };

  useEffect(() => {
    if (!userAnswerData) return;
    let newDict = {}
    for (const obj of userAnswerData.data) {
      newDict[obj.question_number] = obj.is_flagged;
    }
    setFlagDict(newDict);
  }, [userAnswerData])

  const openDrawer = async (open) => {
    if (open) {
      fetchFlagDict();
    }
    setDrawerOpen(open);
  };

  // warp: save current answer quickly (if necessary) then jump to destination
  const handleWarp = async (destQuestionNumber) => {
    // A simple approach: call submitUserAnswer to save current question's cached data if you keep that elsewhere.
    // If you don't keep per-question answer state at parent, the QuestionView takes care of saving before warp
    setQuestionNumber(destQuestionNumber);
    setDrawerOpen(false);
  };

  const handleNavigate = (directionOrTarget) => {
    // if directionOrTarget is 1 or -1 => move relatively, else absolute number
    if (Math.abs(directionOrTarget) === 1) {
      setQuestionNumber((prev) => Math.max(1, Math.min(questionCount, prev + directionOrTarget)));
    } else {
      setQuestionNumber(Math.max(1, Math.min(questionCount, directionOrTarget)));
    }
  };

  const handleCompleteExam = (examComponentBaseId) => {
    navigate(`/exam-launchpad/${examComponentBaseId}`);
  };

  // Wait until examComponentId known
  if (!examComponentId) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading exam...</Typography>
      </Box>
    );
  }

  return (
    <div>
      <Box sx={{ pt: 2, pb: 2, textAlign: "left" }}>
        <Button
          variant="contained"
          sx={{ backgroundColor: "#EBEBEB", color: "#000000", "&:hover": { backgroundColor: "#DBDBDB" } }}
          onClick={() => openDrawer(true)}
        >
          All Questions
        </Button>
      </Box>

      <Drawer open={drawerOpen} onClose={() => openDrawer(false)}>
        <DrawerList
          questionList={questionList}
          onNavigate={(qnum) => handleWarp(qnum)}
          flaggedQuestions={flagDict}
          onClose={openDrawer}
        />
      </Drawer>

      <QuestionView
        examComponentId={parseInt(examComponentId)}
        questionNumber={questionNumber}
        questionCount={questionCount}
        onNavigate={(dirOrAbs) => handleNavigate(dirOrAbs)}
        submitUserAnswer={submitUserAnswer}
        onComplete={() => handleCompleteExam(examComponentBaseId)}
      />
    </div>
  );
};

export default TestApp;
